import isNil from 'lodash/isNil';

import {
  type DateTime,
  calendarDateFromInstant,
  calendarDateTimeFromTimestamp,
  isInstantAfter,
  todayDateTime,
} from './dateutils';
import { DEFAULT_LANGUAGE } from './emailFixedTemplatesUtils';
import { buildDatedFileNameBase } from './fileUtils';
import { getFilterEntityValuesForGroups } from './filterGroupsUtils';
import { getTypedLanguage } from './i18nutils';
import { isLeaseIncludedInManagementFilters } from './leaseutils';
import { isNilOrEmpty } from './objectUtils';
import { isSmsTooLong } from './smsUtils';
import { getNinetyDaysTtlFromDateTime } from './ttlUtils';
import {
  type Communication,
  type FilterGroup,
  type Language,
  type Lease,
  type CommunicationBlockedRecipient,
  CommunicationChannel,
  CommunicationStatus,
  CommunicationType,
  CreditOperationType,
  FilterEntityType,
  TrackingProvider,
  BounceType,
  CommunicationErrorCode,
} from './types';

export const getCreditOperationForChannel = (channel: CommunicationChannel): CreditOperationType | null => {
  switch (channel) {
    case CommunicationChannel.SMS:
      return CreditOperationType.SMS;
    case CommunicationChannel.LETTER:
      return CreditOperationType.LETTER;
    case CommunicationChannel.REGISTERED_LETTER:
      return CreditOperationType.REGISTERED_LETTER;
    case CommunicationChannel.WHATSAPP:
      return CreditOperationType.WHATSAPP;
    default:
      return null;
  }
};

const COMMUNICATION_INACTIVITY_TTL_STATUSES = [
  CommunicationStatus.TO_SEND,
  CommunicationStatus.SNOOZED,
  CommunicationStatus.OUTDATED,
];

export const hasCommunicationInactivityTtl = (communication: Pick<Communication, 'status' | 'sentAt'>): boolean =>
  COMMUNICATION_INACTIVITY_TTL_STATUSES.includes(communication.status) && isNilOrEmpty(communication.sentAt);

export const isCommunicationAwaitingManualSend = (communication: Pick<Communication, 'status' | 'toSend'>): boolean =>
  communication.status === CommunicationStatus.TO_SEND && !communication.toSend;

export const getCommunicationInactivityTtl = (
  floor: DateTime,
  scheduledAt?: string | null,
  snoozedDate?: string | null,
  lastActivityAt?: string | null
): number => {
  const parseInstant = (value?: string | null): DateTime | undefined => {
    if (isNil(value) || value === '') {
      return undefined;
    }
    return calendarDateTimeFromTimestamp(value);
  };
  const base = [parseInstant(scheduledAt), parseInstant(snoozedDate), parseInstant(lastActivityAt)].reduce<DateTime>(
    (latest, current) => (!isNil(current) && isInstantAfter(current, latest) ? current : latest),
    floor
  );
  return getNinetyDaysTtlFromDateTime(base);
};

export const getLetterTrackingLink = (communication?: Communication | null): string | null => {
  if (
    communication?.letterTrackingProvider === TrackingProvider.BPOST &&
    !isNilOrEmpty(communication.letterTrackingCode)
  ) {
    return `${BPOST_TRACKING_PREFIX_URL}${communication.letterTrackingCode}`;
  }
  return null;
};

export const getLetterTrackingProvider = (communication?: Communication | null): string =>
  communication?.letterTrackingProvider ?? '';

export const LETTER_SEND_FAILED_EXTERNAL_STATUS = 'SentToNotificationServiceError';

export const ASSUMED_DELIVERED_EXTERNAL_STATUS = 'AssumedDelivered';

export const POSTBIRD_ERROR_STATUSES = [
  'UploadError',
  'PdfCriticalValidationError',
  'AddressError',
  'PaymentError',
  LETTER_SEND_FAILED_EXTERNAL_STATUS,
  'NormalSendingFailed',
  'Canceled',
  'StatusNotMapped',
];

export const POSTBIRD_DELIVERED_STATUSES = ['Send', 'Delivered'];

export const POSTBIRD_NOT_COLLECTED_STATUSES = ['RegisteredSendingNotDelivered'];

export const isLetterCommunicationInError = (communication?: Communication | null): boolean =>
  !isNilOrEmpty(communication?.externalStatus) && POSTBIRD_ERROR_STATUSES.includes(communication?.externalStatus ?? '');

export const BPOST_TRACKING_PREFIX_URL = 'https://track.bpost.cloud/btr/web/#/search?itemCode=';

export const LETTER_CHANNELS = [CommunicationChannel.LETTER, CommunicationChannel.REGISTERED_LETTER];

export const isLetterChannel = (channel: CommunicationChannel): boolean => LETTER_CHANNELS.includes(channel);

export const isTerminationLetter = (communication: Pick<Communication, 'type' | 'channel'>): boolean =>
  communication.type === CommunicationType.LEASE_TERMINATION && isLetterChannel(communication.channel);

export const PHONE_NUMBER_CHANNELS = [CommunicationChannel.SMS, CommunicationChannel.WHATSAPP];

export const isPhoneNumberChannel = (channel?: CommunicationChannel | null): boolean =>
  !isNil(channel) && PHONE_NUMBER_CHANNELS.includes(channel);

export const hasLetterBeenSent = (letter: Communication): boolean =>
  letter.status === CommunicationStatus.SENT || !isNilOrEmpty(letter.externalStatus);

export const STOPPABLE_LETTER_STATUSES = [
  CommunicationStatus.DRAFT,
  CommunicationStatus.TO_SEND,
  CommunicationStatus.SCHEDULED,
  CommunicationStatus.DOWNLOADED,
  CommunicationStatus.ERROR,
];

export const SENT_LETTER_STATUSES = [
  CommunicationStatus.SENT,
  CommunicationStatus.SENT_MANUALLY,
  CommunicationStatus.SENDING,
];

export const canLetterStillBeStopped = (letter: Pick<Communication, 'status'>): boolean =>
  STOPPABLE_LETTER_STATUSES.includes(letter.status);

export const hasLetterLeftThePlatform = (letter: Communication): boolean =>
  SENT_LETTER_STATUSES.includes(letter.status);

const HISTORY_COMMUNICATION_STATUSES = [
  CommunicationStatus.SENT,
  CommunicationStatus.SENT_MANUALLY,
  CommunicationStatus.DOWNLOADED,
  CommunicationStatus.IGNORED,
];

export const MAX_SEND_RETRY_COUNT = 3;
export const COMMUNICATION_BULK_CONCURRENCY = 25;

const haveAllBlockedRecipientsBounceType = (
  blockedRecipients: CommunicationBlockedRecipient[],
  bounceType: BounceType
): boolean => blockedRecipients.every((blockedRecipient) => blockedRecipient.bounceType === bounceType);

const getPhoneNumberErrorCodeFromBlockedRecipients = (
  blockedRecipients: CommunicationBlockedRecipient[]
): CommunicationErrorCode => {
  if (haveAllBlockedRecipientsBounceType(blockedRecipients, BounceType.INVALID_NUMBER)) {
    return CommunicationErrorCode.SMS_INVALID_NUMBER;
  }
  if (haveAllBlockedRecipientsBounceType(blockedRecipients, BounceType.BLOCKED_BY_RECIPIENT)) {
    return CommunicationErrorCode.SMS_OPTED_OUT;
  }
  return CommunicationErrorCode.SMS_UNDELIVERED;
};

export const getCommunicationErrorCodeFromBlockedRecipients = (
  blockedRecipients?: CommunicationBlockedRecipient[] | null,
  channel?: CommunicationChannel
): CommunicationErrorCode => {
  if (isNilOrEmpty(blockedRecipients)) {
    return CommunicationErrorCode.BOUNCED;
  }
  if (isPhoneNumberChannel(channel)) {
    return getPhoneNumberErrorCodeFromBlockedRecipients(blockedRecipients);
  }
  if (haveAllBlockedRecipientsBounceType(blockedRecipients, BounceType.MAILBOX_FULL)) {
    return CommunicationErrorCode.MAILBOX_FULL;
  }
  return CommunicationErrorCode.BOUNCED;
};

export const SENT_COMMUNICATION_STATUSES = [CommunicationStatus.SENDING, CommunicationStatus.SENT];

export const isSentCommunicationStatus = (status?: CommunicationStatus | null): boolean =>
  !isNil(status) && SENT_COMMUNICATION_STATUSES.includes(status);

export const isHistoryCommunicationStatus = (status?: CommunicationStatus | null): boolean =>
  !isNil(status) && HISTORY_COMMUNICATION_STATUSES.includes(status);

const MANUALLY_HANDLED_COMMUNICATION_STATUSES = [CommunicationStatus.DOWNLOADED, CommunicationStatus.SENT_MANUALLY];

export const isManuallyHandledCommunicationStatus = (status?: CommunicationStatus | null): boolean =>
  !isNil(status) && MANUALLY_HANDLED_COMMUNICATION_STATUSES.includes(status);

const AWAITING_SENDING_COMMUNICATION_STATUSES = [
  CommunicationStatus.TO_SEND,
  CommunicationStatus.SCHEDULED,
  CommunicationStatus.SNOOZED,
];

export const isAwaitingSendingCommunicationStatus = (status?: CommunicationStatus | null): boolean =>
  !isNil(status) && AWAITING_SENDING_COMMUNICATION_STATUSES.includes(status);

export const getCommunicationLanguage = (communication: Communication): Language =>
  getTypedLanguage(communication.lease?.language ?? DEFAULT_LANGUAGE);

interface CommunicationForFileName {
  sentAt?: string | null;
  createdAt?: string | null;
}

export const buildCommunicationFileNameBase = (
  communication: CommunicationForFileName,
  translatedLabel: string
): string => {
  const dateSource = communication.sentAt ?? communication.createdAt;
  const dateTime = isNil(dateSource) ? todayDateTime() : calendarDateTimeFromTimestamp(dateSource);
  return buildDatedFileNameBase(translatedLabel, calendarDateFromInstant(dateTime));
};

export const mergeRecipients = (recipientsList: (string | null | undefined)[]) =>
  recipientsList.filter((recipient) => !isNilOrEmpty(recipient)).join(',');

type CommunicationRecipients = Pick<
  Communication,
  | 'recipient'
  | 'recipientInBcc'
  | 'recipientInCc'
  | 'contactIds'
  | 'recipientContactIdsInBcc'
  | 'recipientContactIdsInCc'
>;

export const hasAtLeastOneRecipient = (communication: CommunicationRecipients) =>
  !isNilOrEmpty(communication.contactIds) ||
  !isNilOrEmpty(communication.recipientContactIdsInBcc) ||
  !isNilOrEmpty(communication.recipientContactIdsInCc) ||
  !isNilOrEmpty(communication.recipient) ||
  !isNilOrEmpty(communication.recipientInBcc) ||
  !isNilOrEmpty(communication.recipientInCc);

export const getMissingRecipientErrorCode = (
  communication: Pick<Communication, 'channel' | 'recipient' | 'recipientInCc' | 'recipientInBcc'>
): CommunicationErrorCode | undefined => {
  switch (communication.channel) {
    case CommunicationChannel.EMAIL:
      return isNilOrEmpty(communication.recipient) &&
        isNilOrEmpty(communication.recipientInCc) &&
        isNilOrEmpty(communication.recipientInBcc)
        ? CommunicationErrorCode.MISSING_EMAIL_ADDRESS
        : undefined;
    case CommunicationChannel.SMS:
      return isNilOrEmpty(communication.recipient) ? CommunicationErrorCode.MISSING_PHONE_NUMBER : undefined;
    default:
      return undefined;
  }
};

export const communicationContentHasText = (content?: string | null) => {
  if (isNilOrEmpty(content)) {
    return false;
  }
  const textContent = content
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;|&zwnj;|&#8204;|&#x200c;|&ZeroWidthSpace;|&#8203;|&#x200b;/gi, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim();
  return !isNilOrEmpty(textContent);
};

type CommunicationForSendEvaluation = Pick<
  Communication,
  | 'status'
  | 'channel'
  | 'toSend'
  | 'recipient'
  | 'recipientInBcc'
  | 'recipientInCc'
  | 'contactIds'
  | 'recipientContactIdsInBcc'
  | 'recipientContactIdsInCc'
  | 'addressId'
  | 'signatureDocumentId'
> &
  Partial<Pick<Communication, 'subject' | 'body'>>;

export const isSendCommunicationEnable = (
  communication: CommunicationForSendEvaluation,
  sendingSourcesWellConfigured: boolean
) => {
  if (
    communication.toSend ||
    [CommunicationStatus.SENDING, CommunicationStatus.SENT, CommunicationStatus.SNOOZED].includes(communication.status)
  ) {
    return false;
  }
  if (communication.channel === CommunicationChannel.EMAIL && !sendingSourcesWellConfigured) {
    return false;
  }
  return hasCommunicationAllAttributesToAllowSending(communication, false);
};

export const hasCommunicationAllAttributesToAllowSending = (
  communication: CommunicationForSendEvaluation,
  sendCommunicationIsAlreadyRequested: boolean
) => {
  if (sendCommunicationIsAlreadyRequested && !communication.toSend) {
    return false;
  }
  switch (communication.channel) {
    case CommunicationChannel.LETTER:
    case CommunicationChannel.REGISTERED_LETTER:
      return hasAtLeastOneRecipient(communication) && communicationContentHasText(communication.body);
    case CommunicationChannel.SMS:
      // it will always be disabled in the communication list since body in not in the light communications properties
      return (
        hasAtLeastOneRecipient(communication) && !isNilOrEmpty(communication.body) && !isSmsTooLong(communication.body)
      );
    case CommunicationChannel.EMAIL:
      return (
        hasAtLeastOneRecipient(communication) &&
        communicationContentHasText(communication.subject) &&
        communicationContentHasText(communication.body)
      );
    default:
      return false;
  }
};

export const isCommunicationInManagementFilter = (
  managementFilters: FilterGroup[],
  communication: Communication,
  allCompleteLeases: Lease[],
  unitIdsToKeep: string[]
) => {
  if (communication.leaseId) {
    const lease = allCompleteLeases.find(({ id }) => communication.leaseId === id);
    return lease && isLeaseIncludedInManagementFilters(lease, managementFilters);
  }
  if (communication.unitId) {
    return unitIdsToKeep.includes(communication.unitId);
  }
  return true;
};

export const filterCommunicationsForManagementFilter = (
  managementFilters: FilterGroup[],
  communications: Communication[],
  allCompleteLeases: Lease[]
) => {
  const unitIdsToKeep = getFilterEntityValuesForGroups(managementFilters, FilterEntityType.UNIT, 'id');
  return communications.filter((communication) =>
    isCommunicationInManagementFilter(managementFilters, communication, allCompleteLeases, unitIdsToKeep)
  );
};
